import { EarningsScenarioResponse } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { HighLowChart } from '@design/charts/high-low-chart/HighLowChart';
import { Button } from '@design/components/button/Button';
import { useElementSize } from '@helpers/hooks/use-element-size/useElementSize';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '@terminal/modules/tab-content/types';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EarningState, Expectation } from '../types';
import { getEarningState, getScenario } from '../utils';
import { ChartEmptyStates } from './ChartEmptyStates';
import { ChartFooter } from './chart-footer/ChartFooter';
import { ChartLegend } from './chart-legend/ChartLegend';
import { ChartTitle } from './chart-title/ChartTitle';
import styles from './EarningsChart.module.scss';

const padding = {
  left: 85,
  right: 150,
};

export interface EarningsChartProps {
  expectation: Expectation;
  earningsData: EarningsScenarioResponse;
  entity: Entity;
  source: PosthogTrackingSource;
}

export const EarningsChart = ({
  expectation,
  earningsData,
  entity,
  source,
}: EarningsChartProps) => {
  const { t } = useTranslation('earnings');
  const chartWrapperRef = useRef(null);
  const { width } = useElementSize(chartWrapperRef);
  const { navigateToPage } = useNavigateToAssetOverview();

  const scenario = getScenario(earningsData, expectation);
  const mappedData = scenario?.data.map(item => ({
    high: item.high / 100,
    median: item.median / 100,
    low: item.low / 100,
    label: t(item.period),
  }));

  if (!scenario || !mappedData) {
    return (
      <ChartEmptyStates
        earningsData={earningsData}
        expectation={expectation}
        scenario={scenario}
      />
    );
  }

  const historyMetaCount = scenario.history?.meta.count;
  const searchParams = new URLSearchParams();

  const { eps } = earningsData;

  const isBeat = eps.surprise
    ? getEarningState(eps) === EarningState.Beat
    : expectation === Expectation.Beat;

  searchParams.append(queryKeys.epsFilter, isBeat ? 'beats' : 'misses');

  if (eps.surprise) {
    searchParams.append(queryKeys.epsValue, eps.surprise.toString());
  }

  return (
    <div className={styles.earningsChartRoot} data-testid="earnings-chart">
      <div className={styles.title}>
        <ChartTitle
          count={historyMetaCount}
          expectation={expectation}
          earningsData={earningsData}
        />
        {scenario && (
          <Button
            data-testid="dig-into-button"
            type="button"
            size="small"
            label={t('earnings:digIntoTheData')}
            onClick={() => {
              navigateToPage({
                entityTag: entity.tag,
                source,
                tab: TabsType.Earnings,
                searchParams,
                replace: true,
              });
            }}
          />
        )}
      </div>
      <div className={styles.content}>
        <ChartLegend />
        <div
          className={styles.chartWrapper}
          ref={chartWrapperRef}
          data-testid="chart-container"
        >
          <HighLowChart
            data={mappedData}
            width={width}
            height={216}
            padding={padding}
          />
        </div>
        <ChartFooter />
      </div>
    </div>
  );
};
